import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonLanguageLabel, CommonMasterClockLabel } from '@models/system-setting';
import { TranslateService } from '@ngx-translate/core';
import { CommonService } from '@service/common.service';
import { commonLanguages, CommonLanguageType, MasterClockType } from 'icreate/common/models/common';
import { Status } from 'icreate/common/models/editor-viewer-element';
import { getValidationErrors, ValidationErrorMap } from 'icreate/common/validators';
import { commonValidator } from 'icreate/common/validators/common/common-validator';
import { mapErrors } from 'icreate/common/validators/custom-functions';
import { drawingIdLenLimit, drawingIdValidation } from 'icreate/common/validators/vessel/vessel-validator';
import _ from 'lodash';
import { VesselService } from '@service/vessel.service';
import { SimpleDropdownOption } from '@components/simple-dropdown/simple-dropdown.component';

export enum CommonInputName {
    drawingId = 'drawingId',
    language = 'language',
    masterClockType = 'masterClockType',
    UtcEnable = 'UtcEnable',
}

@Component({
    selector: 'app-common-dialog',
    templateUrl: './common-dialog.component.html',
    styleUrls: ['./common-dialog.component.css'],
    standalone: false,
})
export class CommonDialogComponent {
    readonly DEFAULT_LANGUAGE = CommonInputName.language;
    readonly MASTER_CLOCK_TYPE = CommonInputName.masterClockType;
    readonly UTC_ENABLE = CommonInputName.UtcEnable;
    readonly DRAWING_ID = CommonInputName.drawingId;

    readonly COMMON_LANGUAGE_LIST = Object.values(CommonLanguageLabel);
    readonly COMMON_SYSTEM_CLOCK = Object.values(CommonMasterClockLabel);

    readonly ENGLISH = CommonLanguageLabel.EN;
    readonly JAPANESE = CommonLanguageLabel.JP;

    validationErrors: ValidationErrorMap = {};
    masterClockTypeLabel: string;
    currentLanguageLabel: string;
    currentJapanCoastGuard: Status;
    currentUtcEnable: Status | boolean;
    isLoading: boolean = false;

    initalData;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            id: string;
            vesselId: string;
            drawingId: string;
            language: CommonLanguageType;
            masterClockType: MasterClockType;
            UtcEnable: Status;
            japanCoastGuard: Status;
            isJrcsEmployee: boolean;
        },
        private dialogRef: MatDialogRef<CommonDialogComponent>,
        private commonService: CommonService,
        private translate: TranslateService,
        private vesselService: VesselService
    ) {}

    ngOnInit() {
        this.masterClockTypeLabel = this.data.masterClockType;
        this.currentLanguageLabel = this.data.language;
        this.currentJapanCoastGuard = this.data.japanCoastGuard;
        this.currentUtcEnable = this.data.UtcEnable;

        this.initalData = _.cloneDeep(this.data);

        this.checkInputErrors();
    }

    onInputChange(event: { name; value }) {
        const { name, value } = event;
        if (name === this.DEFAULT_LANGUAGE) {
            this.currentLanguageLabel = value;
            this.data[name] = value;
        } else if (name === this.MASTER_CLOCK_TYPE) {
            this.data.masterClockType = value;
        } else {
            this.data[event.name] = event.value;
        }

        this.checkInputErrors();
    }

    changeUtcEnable() {
        this.data.UtcEnable = this.toStatus(this.currentUtcEnable);
        this.checkInputErrors();
    }

    changeJapanCoastGuard() {
        this.data.japanCoastGuard = this.toStatus(this.currentJapanCoastGuard);
        this.checkInputErrors();
    }

    disabledSaveButton() {
        return (
            _.isEqual(this.initalData, this.data) || !this.data.drawingId || !!Object.keys(this.validationErrors).length
        );
    }

    close() {
        this.dialogRef.close();
    }

    async saveCommon() {
        try {
            this.isLoading = true;
            if (this.initalData.drawingId !== this.data.drawingId) {
                await this.vesselService.updateDrawingId(this.data.vesselId, this.data.drawingId);
            }
            const commonHasChanged = this.commonObjectChanged();
            if (commonHasChanged) {
                const commonData = {
                    id: this.data.id,
                    vesselId: this.data.vesselId,
                    language: this.data.language,
                    masterClockType: this.data.masterClockType,
                    UtcEnable: this.data.UtcEnable,
                    japanCoastGuard: this.data.japanCoastGuard,
                };
                if (this.data.id) {
                    await this.commonService.update(commonData);
                } else {
                    await this.commonService.create(commonData);
                }
            }

            this.close();
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }
    }

    checkInputErrors() {
        this.validationErrors = {};
        const errors = getValidationErrors(commonValidator(), {
            language: this.data.language,
            masterClockType: this.data.masterClockType,
            UtcEnable: this.data.UtcEnable,
            japanCoastGuard: this.data.japanCoastGuard,
        });

        if (errors) this.validationErrors = mapErrors(errors);

        const drawingIdError = getValidationErrors(drawingIdValidation(), {
            drawingId: this.data.drawingId,
        });

        const mappedErrors = mapErrors(drawingIdError);

        if (mappedErrors?.drawingId) {
            if (mappedErrors.drawingId.errors[0] == 'errors.validation.generic.above-maximum') {
                mappedErrors.drawingId.message = this.translate.instant('errors.validation.generic.above-maximum', {
                    max: drawingIdLenLimit,
                });
            }
            this.validationErrors = {
                ...this.validationErrors,
                ...mappedErrors,
            };
        }
    }

    private toStatus(value: Status | boolean): Status {
        return value ? 1 : 0;
    }

    private commonObjectChanged() {
        return Object.keys(this.initalData).some(key => {
            if (key === CommonInputName.drawingId) return false;
            return this.initalData[key] !== this.data[key];
        });
    }

    get commonMasterClockOptions(): SimpleDropdownOption[] {
        return [
            {
                value: MasterClockType.MasterPulse,
                string: 'system-settings.common.clock.master-pulse.label',
            },
            {
                value: MasterClockType.MasterComm,
                string: 'system-settings.common.clock.master-comm.label',
            },
            {
                value: MasterClockType.Internal,
                string: 'system-settings.common.clock.internal.label',
            },
        ];
    }

    get commonLanguageOptions(): SimpleDropdownOption[] {
        return [
            {
                value: commonLanguages.EN,
                string: CommonLanguageLabel.EN,
            },
            {
                value: commonLanguages.JP,
                string: CommonLanguageLabel.JP,
            },
        ];
    }
}
